import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { outreachApi } from '../../api/outreach'
import { voiceApi } from '../../api/voice'
import { RefusedError } from '../../api/client'
import { Correct } from '../../components/Correct'
import { ResearchCorrected } from '../../components/Research/CorrectedNote'
import { useJobLive } from '../../hooks/useJobLive'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate, formatMoney } from '../../lib/format'
import type { OutreachPersonView } from '../../types/outreach'
import type { DraftView } from '../../types/voice'
import { Brief, History, KnowThem } from './DrawerParts'
import { Compose } from './Compose'

// One person, opened. Four questions, in the order a partner asks them: do I know this person, what have
// we actually said to each other, who are they, and what do I say next. Everything shown traces to a
// message, a meeting or the pipeline record; what is not on file is said to be.

export function PersonDrawer({ personId, onClose }: { personId: string; onClose: () => void }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['outreach-person', spoke, personId], queryFn: () => outreachApi.person(spoke, personId), staleTime: 10_000 })
  const [jobId, setJobId] = useState<string | null>(null)
  useEffect(() => { setJobId(null) }, [personId])
  const research = useMutation({ mutationFn: () => outreachApi.research(spoke, personId), onSuccess: (r) => setJobId(r.job.id) })
  const liveId = jobId ?? q.data?.activeResearchJobId ?? undefined
  const { state: live } = useJobLive(spoke, liveId)
  const landed = live ? live.job.status === 'Completed' || live.job.status === 'Failed' || live.job.status === 'Cancelled' : false
  useEffect(() => {
    if (!landed) return
    void qc.invalidateQueries({ queryKey: ['outreach-person', spoke, personId] })
  }, [landed, qc, spoke, personId])
  const researching = !!live && !landed

  if (q.isPending) return <aside className="outreach-drawer"><p className="muted small">Loading…</p></aside>
  if (q.isError || !q.data) {
    return (
      <aside className="outreach-drawer">
        <div className="refusal">
          <strong>Not on your list</strong>
          <p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error?.message ?? 'Nothing here.'}</p>
        </div>
      </aside>
    )
  }

  const v = q.data
  const p = v.person
  // A brief the person has said is wrong is not shown: they told Scout it was about somebody else of the
  // same name, or carried a wrong fact, and showing it anyway would be the app arguing with them.
  const noteId = `person:${p.personId}`
  const corrected = !!v.research && v.research.status !== 'current'

  return (
    <aside className="outreach-drawer">
      <div className="actions space-between">
        <div>
          <p className="kicker">{p.persona ?? p.role ?? 'contact'}{v.company ? ` · ${v.company.name}` : ''}</p>
          <h2>{p.name}</h2>
          {p.title && <p className="muted">{p.title}</p>}
        </div>
        <button className="button small" onClick={onClose}>Close</button>
      </div>

      {v.item && (
        <div className={`outreach-drawer-reason ${v.item.reasonKind}`}>
          <p><strong>Why now.</strong> {v.item.reason}.</p>
          {v.item.band && <p className="muted small">{v.item.band.label} · {v.item.band.cadence}. {v.item.band.basis}</p>}
          <Correct kind="outreach-reason" entityId={`${p.personId}:${v.item.reasonKind}`} entityLabel={`${p.name} — ${v.item.reason}`}
            currentValue={v.item.reasonKind} label="Not why I'd call them" />
        </div>
      )}

      <KnowThem a={v.acquaintance} email={p.email} />

      <Actions view={v} personId={personId} researching={researching} onResearch={() => research.mutate()} />
      <Compose personId={personId} hasAddress={!!p.email} modelConfigured={v.modelConfigured} />
      {research.isError && <p className="error small">{research.error instanceof RefusedError ? research.error.refusal.message : (research.error as Error).message}</p>}

      <section className="outreach-section">
        <h3>What you have said to each other</h3>
        {v.history.length === 0 ? (
          <p className="muted small">Nothing on file: no mail either way and no meeting. {v.mailboxConnected ? 'They came from a list you imported.' : 'Your mailbox is not connected yet.'}</p>
        ) : (
          <History history={v.history} />
        )}
      </section>

      {v.pursuits.length > 0 && (
        <section className="outreach-section">
          <h3>Pursuits at {v.company?.name ?? 'the account'}</h3>
          <ul className="plain">
            {v.pursuits.slice(0, 6).map((e) => (
              <li key={e.engagementId}>
                <Link to={`/${e.stage === 'lead' || e.stage === 'dormant' ? 'leads' : 'opportunities'}/${encodeURIComponent(e.engagementId)}`}><strong>{e.subject}</strong></Link>
                <span className="muted small"> · {e.stage}{e.amount != null ? ` · ${formatMoney(e.amount)}` : ''}{e.lastActivityOn ? ` · ${formatDate(e.lastActivityOn)}` : ''}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="outreach-section research-card">
        <div className="actions space-between">
          <h3>Who they are</h3>
          {/* The brief is an assertion like any other. "Can the research be corrected if our research agent
              found the wrong person with the same name?" — this is where that is said. */}
          {v.research && <Correct kind="research" entityId={noteId} entityLabel={`The brief about ${p.name}`} label="Not right?" />}
        </div>
        <p className="muted small">
          A brief written from what is on file about them — the mail both ways, their company and its pursuits, the meetings, the signals.
          No web: anything the file does not carry is said to be, and any source that is not your own mail is named.
        </p>
        {researching && live && (
          <div className="research-live">
            <p className="kicker">Reading the file · <Link to={`/jobs/${encodeURIComponent(live.job.id)}`}>open the console</Link></p>
            <ol className="research-steps">
              {live.job.steps.map((s) => <li key={s.stepId} className={s.status.toLowerCase()}><span>{s.label}</span>{s.message && <em>{s.message}</em>}</li>)}
            </ol>
          </div>
        )}
        {live && live.job.status === 'Failed' && (
          <div className="refusal" role="alert">
            <strong>The brief could not be written.</strong>
            <p>{live.job.error ?? live.job.steps.find((s) => s.status === 'Failed')?.message ?? 'The job failed.'}</p>
            <p className="muted small"><Link to={`/jobs/${encodeURIComponent(live.job.id)}`}>Open the console</Link> for the full record, then try again.</p>
          </div>
        )}
        {v.research && corrected && (
          <ResearchCorrected entityId={noteId} correctionId={v.research.correctionId} status={v.research.status} what="brief"
            researching={researching} canResearch={v.modelConfigured} onResearch={() => research.mutate()} />
        )}
        {!researching && !v.research && (
          <div className="research-empty">
            <p>No brief yet.</p>
            <button className="button primary" onClick={() => research.mutate()} disabled={research.isPending || !v.modelConfigured}>
              {research.isPending ? 'Starting…' : 'Research this person'}
            </button>
            {!v.modelConfigured && <p className="muted small">Needs the CLI controller or an API model.</p>}
          </div>
        )}
        {v.research && !corrected && <Brief note={v.research.note} writtenAt={v.research.writtenAt} modelId={v.research.modelId} stale={v.researchStale} />}
      </section>
    </aside>
  )
}

/* ── pieces ── */

/** Ready to act on: a draft in their own voice, a play, or the account behind the person. */
function Actions({ view, personId, researching, onResearch }: { view: OutreachPersonView; personId: string; researching: boolean; onResearch: () => void }) {
  const spoke = useSpoke()
  const [draft, setDraft] = useState<DraftView | null>(null)
  const [edited, setEdited] = useState('')
  useEffect(() => { setDraft(null); setEdited('') }, [personId])
  const write = useMutation({
    mutationFn: () => voiceApi.draft(spoke, null, view.draftBrief ?? `Write to ${view.person.name}.`, view.person.name),
    onSuccess: (d) => { setDraft(d); setEdited(d.draftText) },
  })
  const teach = useMutation({ mutationFn: () => voiceApi.feedback(spoke, draft!.id, edited) })

  return (
    <div className="outreach-actions">
      <div className="actions">
        <button className="button primary" onClick={() => write.mutate()} disabled={write.isPending || !view.modelConfigured || !view.draftBrief}
          title={view.modelConfigured ? 'A draft written in your own voice, from the reason above and the last thing each of you said. Nothing it says is invented.' : 'Drafting needs the CLI controller or an API model.'}>
          {write.isPending ? 'Writing…' : 'Draft in my voice'}
        </button>
        <button className="button" onClick={onResearch} disabled={researching || !view.modelConfigured}>
          {researching ? 'Researching…' : view.research ? 'Research again' : 'Research this person'}
        </button>
        {view.item?.openPursuit
          ? <Link className="button" to={`/${view.item.openPursuit.stage === 'lead' ? 'leads' : 'opportunities'}/${encodeURIComponent(view.item.openPursuit.engagementId)}#plays`}>Run a play</Link>
          : <Link className="button" to="/playbook">Run a play</Link>}
        {view.item?.companyId && <Link className="button" to={`/workbench/${encodeURIComponent(view.item.companyId)}?person=${encodeURIComponent(personId)}`}>Open in workbench</Link>}
      </div>
      {!view.modelConfigured && <p className="muted small">Drafting and briefs need the CLI controller or an API model. <Link to="/voice">Your voice</Link> is built either way.</p>}
      {write.isError && <p className="error small">{write.error instanceof RefusedError ? write.error.refusal.message : (write.error as Error).message}</p>}
      {draft && (
        <div className="outreach-draft">
          <p className="kicker">In your voice · from the reason above and what you each said last</p>
          <textarea rows={8} value={edited} onChange={(e) => setEdited(e.target.value)} aria-label="Draft" />
          <div className="actions">
            <button className="button small" onClick={() => teach.mutate()} disabled={teach.isPending || edited === draft.draftText}>
              {teach.isPending ? 'Saving…' : teach.isSuccess ? 'Saved' : 'This is what I would send'}
            </button>
            <Link className="button small" to="/voice">Open the workbench</Link>
          </div>
          <p className="muted small">Editing it teaches the profile: a large edit becomes a preference the next draft is written under.</p>
        </div>
      )}
    </div>
  )
}
